import { Grid } from '@mui/material';
import { HSpacer, RoutalLoader, RoutalPalette } from '@nx-smartmonkey/ui';
import { useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import ExpandLessIcon from '../../../../../assets/svg/Plan/overview/ExpandLessIcon';
import ExpandMoreIcon from '../../../../../assets/svg/Plan/overview/ExpandMoreIcon';

import { IconDriversCreateMenu } from '../../../../../components/menus/IconDriversCreateMenu';
import { IconStopsCreateMenu } from '../../../../../components/menus/IconStopsCreateMenu';
import { useRetrieveActivePlan } from '../../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanStops } from '../../../../../hooks/plan/useRetrieveActivePlanStops';
import { useSetActivePlanSelectedStops } from '../../../../../hooks/plan/useSetActivePlanSelectedStops';
import { useUpdateDriverStops } from '../../../../../hooks/plan/useUpdateDriverStops';
import Text from '../../../../../react-components/Text';
import { DriversList } from './DriversList';
import { UnassignedStopsList } from './UnassignedStopsList';

const PlanOverviewListLoading = () => {
  return (
    <PlanOverviewListContainer wrap="nowrap" container direction="column" style={{ overflow: `hidden` }}>
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
        style={{ height: `100%`, width: `100%` }}
      >
        <RoutalLoader />
      </Grid>
    </PlanOverviewListContainer>
  );
};

const PlanOverviewListContainer = styled(Grid)`
  height: calc(100vh - 64px);
  width: 380px !important;
  background-color: ${RoutalPalette.neutral00};
  border-right: 1px solid ${RoutalPalette.neutral10};
  overflow-y: auto;
`;

const AccordionHeader = styled(Grid)`
  height: 50px;
  margin-top: -1px;
  padding: 8px;
  cursor: pointer;
  background-color: ${RoutalPalette.neutral00};
  border-top: 1px solid ${RoutalPalette.neutral10};
  border-bottom: 1px solid ${RoutalPalette.neutral10};
`;

export const PlanOverviewList = () => {
  const [driversExpanded, setDriversExpanded] = useState<boolean>(true);
  const [unassignedStopsExpanded, setUnassignedStopsExpanded] = useState<boolean>(true);

  const { data: plan, isLoading: isPlanLoading } = useRetrieveActivePlan();
  const { data: stops, isLoading: isStopsLoading, map: stopsMap } = useRetrieveActivePlanStops();
  const { data: drivers, isLoading: isDriversLoading, map: driversMap } = useRetrieveActivePlanDrivers();
  const { selectedStops: selectedStopsIds, setSelectedStops } = useSetActivePlanSelectedStops();

  const { run: updateDriverStops } = useUpdateDriverStops();

  if (isPlanLoading || !plan || !drivers || !stops) {
    return <PlanOverviewListLoading />;
  }

  return (
    <PlanOverviewListContainer wrap="nowrap" container direction="column">
      <DragDropContext
        onDragStart={(initial) => {
          if (!selectedStopsIds.includes(initial.draggableId)) {
            setSelectedStops([initial.draggableId]);
          }
        }}
        onDragEnd={({ draggableId: stopId, source, destination }) => {
          if (destination) {
            const { droppableId: driverIdTo, index: indexTo } = destination;
            const selectedStops = selectedStopsIds
              .map((stopId) => stopsMap[stopId])
              .filter((stop) => stop !== undefined);
            updateDriverStops({ driver: driversMap[driverIdTo], stops: selectedStops, index: indexTo });
          }
        }}
      >
        {/* Drivers */}
        <AccordionHeader
          container
          alignContent="center"
          onClick={() => {
            setDriversExpanded(!driversExpanded);
          }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid container direction="row" alignItems="center" style={{ height: `32px`, width: `auto` }}>
            {driversExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <HSpacer small />
            <Text style={{ color: RoutalPalette.secondary20, fontSize: `14px`, fontWeight: 500 }}>
              <FormattedMessage id="planner.plans.table.total_drivers" />
            </Text>
            <HSpacer small />
            <Text style={{ fontWeight: 500, color: RoutalPalette.secondary40, fontSize: `14px` }}>
              {drivers.length}
            </Text>
          </Grid>
          <IconDriversCreateMenu />
        </AccordionHeader>
        {driversExpanded && !isDriversLoading && <DriversList drivers={drivers} />}

        {/* Unassigned stops */}
        <AccordionHeader
          container
          alignContent="center"
          onClick={() => {
            setUnassignedStopsExpanded(!unassignedStopsExpanded);
          }}
          justifyContent="space-between"
          alignItems="center"
          wrap="nowrap"
        >
          <Grid container direction="row" alignItems="center" style={{ height: `32px`, width: `auto` }}>
            {unassignedStopsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <HSpacer small />
            <Text style={{ color: RoutalPalette.secondary20, fontSize: `14px`, fontWeight: 500 }}>
              <FormattedMessage id="planner.plans.table.total_stops" />
            </Text>
            <HSpacer small />
            <Text style={{ fontWeight: 500, color: RoutalPalette.secondary40, fontSize: `14px` }}>
              {stops.filter((stop) => !stop.driver_id).length}
            </Text>
          </Grid>
          <IconStopsCreateMenu id="list-stops-create-actions" style={{ height: `32px`, width: `52px`, padding: 0 }} />
        </AccordionHeader>
        {unassignedStopsExpanded && !isStopsLoading && (
          <UnassignedStopsList stops={stops.filter((stop) => !stop.driver_id)} />
        )}
      </DragDropContext>
    </PlanOverviewListContainer>
  );
};
